import { Component, OnInit, inject } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { PartnerPlayedFetchResult, PartnerPlayedResponse, PartnerPlayedRoom } from 'src/app/models/partner-played.model';
import { TerpecaRoom } from 'src/app/models/room.model';
import { AccountIdFieldInfo, TerpecaUser } from 'src/app/models/user.model';
import { AuthService } from 'src/app/services/auth.service';
import { PartnerEndpointsService } from 'src/app/services/partner-endpoints.service';
import { RoomRestrictedService } from 'src/app/services/room-restricted.service';
import { SettingsService } from 'src/app/services/settings.service';
import { environment } from 'src/environments/environment';

export interface PartnerPlayedDialogData {
  user: TerpecaUser;
  partner: AccountIdFieldInfo;
}

/** One row of the resolved-room table: the partner's entry next to our record for that id, if we have one. */
interface ResolvedPlayedRoom {
  entry: PartnerPlayedRoom;
  room?: TerpecaRoom;
  awardStatus: string;
}

const ROOM_FETCH_CHUNK_SIZE = 100;

/**
 * Owner-only review of what a partner site reports for an applicant's played rooms: the raw diagnostic envelope
 * from fetchPartnerPlayedRooms plus each returned room resolved against our own rooms collection. This is the one
 * place `flagged` is shown; it never reaches the voter-facing UI.
 */
@Component({
  selector: 'app-partnerplayeddialog',
  standalone: false,
  templateUrl: './partnerplayeddialog.component.html',
  styleUrl: './partnerplayeddialog.component.css'
})
export class PartnerPlayedDialogComponent implements OnInit {
  dialogRef = inject<MatDialogRef<PartnerPlayedDialogComponent>>(MatDialogRef);
  data = inject<PartnerPlayedDialogData>(MAT_DIALOG_DATA);
  private auth = inject(AuthService);
  private db = inject(AngularFirestore);
  private partnerEndpoints = inject(PartnerEndpointsService);
  private restrictedRooms = inject(RoomRestrictedService);
  private settings = inject(SettingsService);

  year = environment.currentAwardYear;
  loading = true;
  /** A TERPECA-side failure (not an owner, no endpoint, secret unreadable...); partner-side errors live in `result`. */
  errorMessage = '';
  result: PartnerPlayedFetchResult | null = null;
  /** The partner's body when it parsed as a played-rooms response, otherwise null (see `rawBody`). */
  response: PartnerPlayedResponse | null = null;
  rawBody = '';
  rows: ResolvedPlayedRoom[] = [];
  columns = ['ourRoom', 'theirRoom', 'status', 'rank', 'rating', 'flags', 'link'];

  get partnerUserId(): string {
    return String(this.data.user[this.data.partner.field] || '').trim();
  }

  get profileUrl(): string {
    return this.data.partner.profileUrl ? this.data.partner.profileUrl(this.partnerUserId) : '';
  }

  get flaggedCount(): number {
    return this.rows.filter(row => row.entry.flagged).length;
  }

  get maybePlayedCount(): number {
    return this.rows.filter(row => row.entry.maybePlayed).length;
  }

  get unresolvedCount(): number {
    return this.rows.filter(row => !row.room).length;
  }

  ngOnInit() {
    this.load();
  }

  private async load() {
    try {
      this.result = await this.partnerEndpoints.fetchPlayedRooms(this.data.partner.partnerId, this.data.user.uid);
      this.response = this.parseResponse(this.result.body);
      // For a translated partner, show what they actually sent, not our translation.
      const raw = this.result.translation ? this.result.partnerBody : this.result.body;
      this.rawBody = typeof raw === 'string' ? raw : JSON.stringify(raw, null, 2);
      if (this.response) {
        this.rows = await this.resolveRooms(this.response.rooms);
      }
    } catch (error) {
      this.errorMessage = (<Error>error)?.message || String(error);
    } finally {
      this.loading = false;
    }
  }

  private parseResponse(body: unknown): PartnerPlayedResponse | null {
    if (!body || typeof body !== 'object' || !Array.isArray((<PartnerPlayedResponse>body).rooms)) {
      return null;
    }
    const response = <PartnerPlayedResponse>body;
    return { ...response, rooms: response.rooms.filter(entry => entry && typeof entry === 'object') };
  }

  private async resolveRooms(entries: PartnerPlayedRoom[]): Promise<ResolvedPlayedRoom[]> {
    const ids = [...new Set(entries.map(entry => String(entry.terpecaID || '')).filter(id => id))];
    const roomsById = new Map<string, TerpecaRoom>();
    for (let i = 0; i < ids.length; i += ROOM_FETCH_CHUNK_SIZE) {
      const chunk = ids.slice(i, i + ROOM_FETCH_CHUNK_SIZE);
      const snapshots = await Promise.all(chunk.map(async id => this.db.firestore.collection('rooms').doc(id).get()));
      for (const doc of snapshots) {
        if (doc.exists) {
          const room = <TerpecaRoom>(doc.data() || {});
          room.docId = doc.id;
          roomsById.set(doc.id, room);
        }
      }
    }
    // Owners can read roomRestricted, which is where the current year's nominee/finalist flags live until published.
    const rooms = await this.restrictedRooms.overlayLoadedRooms([...roomsById.values()]);
    for (const room of rooms) {
      roomsById.set(room.docId, room);
    }
    return entries.map(entry => {
      const room = roomsById.get(String(entry.terpecaID || ''));
      return { entry, room, awardStatus: room ? this.awardStatus(room) : '' };
    });
  }

  /** Where the room stands this year, falling back to last year's official result so the column is rarely empty. */
  private awardStatus(room: TerpecaRoom): string {
    for (const year of [this.year, this.year - 1]) {
      if ((room.isWinner || []).includes(year)) {
        const results = room.resultsData?.[year];
        if (results?.unranked) {
          return `${year} winner (NR)`;
        }
        const rank = results?.rank;
        return rank !== undefined ? `${year} winner (#${rank})` : `${year} winner`;
      }
      if ((room.isFinalist || []).includes(year)) {
        return `${year} finalist`;
      }
      if ((room.isNominee || []).includes(year)) {
        return `${year} nominee`;
      }
      if (year === this.year && this.candidatesExist() && this.isCandidate(room, year)) {
        return `${year} candidate`;
      }
    }
    return '';
  }

  /** Mirrors loadSeason() in the awards feed: candidates only exist between nominations closing and voting opening. */
  private candidatesExist(): boolean {
    return !this.settings.isNominationOpen(this.auth.currentUser) && !this.settings.isVotingOpen()
        && !this.settings.isFinishedYear(this.year);
  }

  /** Mirrors isCurrentYearCandidate() in the awards feed: any current-year nomination, or an automatic finalist. */
  private isCandidate(room: TerpecaRoom, year: number): boolean {
    const nominated = (room.nominations || []).some(nomination => nomination.year === year);
    const automaticFinalist = year >= 2022 && (room.isWinner || []).includes(year - 1)
        && (room.isApproved || []).includes(year) && !(room.isIneligible || []).includes(year);
    return nominated || automaticFinalist;
  }

  roomLabel(room: TerpecaRoom): string {
    return room.englishName && room.englishName !== room.name ? `${room.englishName} [${room.name}]` : room.name;
  }

  roomLocation(room: TerpecaRoom): string {
    return [room.city, room.state, room.country].filter(part => part).join(', ');
  }

  close() {
    this.dialogRef.close();
  }
}
